import * as React from "react";
import { Slot } from "../lib/slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2Icon } from "lucide-react";

import { cn } from "#/utils";

const buttonVariants = cva(
  "focus-visible:ring-ring inline-flex items-center justify-center rounded-full text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-xs",
        outline:
          "border-input hover:bg-accent hover:text-accent-foreground border bg-transparent shadow-xs",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-xs",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        heartbeat:
          "border hover:bg-accent hover:text-accent-foreground animate-heartbeat",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonElement = HTMLButtonElement;
export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  };
const Button = React.forwardRef<ButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    // loading은 "처리 중"이므로 클릭도 막는다 — 스피너만 돌고 클릭이 먹으면 중복 제출이
    // 난다(가드했다고 착각하게 만드는 게 더 위험하다). disabled와 OR로 합산하므로
    // `disabled={!canSubmit}`처럼 다른 조건을 넘긴 호출자도 loading 가드를 함께 받는다.
    const isDisabled = Boolean(disabled) || loading;
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          // asChild(<a> 등)는 :disabled 의사클래스가 없어 cva의 disabled: 가드가 안 걸린다.
          asChild && isDisabled && "pointer-events-none opacity-50",
        )}
        ref={ref}
        {...(asChild
          ? { "aria-disabled": isDisabled || undefined }
          : { disabled: isDisabled })}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <Loader2Icon
            className={cn("size-4 animate-spin", !!children && "mr-2")}
          />
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
